import { useState, type CSSProperties } from 'react';
import { getLegalSections } from '../../legal/index.js';
import { useT } from '../../i18n/index.jsx';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';

/**
 * Consent-Objekt, das beim Accept an den Caller zurückgegeben wird.
 * Wird z.B. in LocalStorage persistiert (DSGVO Art. 7).
 */
export interface CookieConsent {
  /** Immer `true` — technisch notwendige Cookies sind nicht abwählbar. */
  required: true;
  analytics: boolean;
  marketing: boolean;
  /** ISO-Timestamp der Einwilligung. */
  timestamp: string;
}

type AcceptMode = 'all' | 'selected' | 'reject';

interface CookieBannerProps {
  onAccept: (consent: CookieConsent) => void;
}

interface ToggleProps {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}

function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <div
      onClick={disabled || !onChange ? undefined : () => onChange(!checked)}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        background: checked ? 'var(--accent)' : 'var(--border2)',
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative' as const,
        transition: 'background .2s',
        opacity: disabled ? 0.6 : 1,
        flexShrink: 0,
      }}>
      <div
        style={{
          position: 'absolute' as const,
          top: 2,
          left: checked ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .2s',
          boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        }} />
    </div>
  );
}

/**
 * CookieBanner — DSGVO-konformer Cookie-Banner mit Kategorien.
 *
 * Bietet drei Accept-Modi: Alle akzeptieren, Auswahl akzeptieren, Alle ablehnen.
 * Verlinkt zum Datenschutz-Volltext (Umschalten innerhalb desselben Modals).
 */
export function CookieBanner({ onAccept }: CookieBannerProps) {
  const t = useT();
  const [analytics, setAnalytics] = useState<boolean>(false);
  const [marketing, setMarketing] = useState<boolean>(false);
  const [showDsgvo, setShowDsgvo] = useState<boolean>(false);

  const accept = (mode: AcceptMode): void => {
    const consent: CookieConsent = {
      required: true,
      analytics: mode === 'all' ? true : mode === 'reject' ? false : analytics,
      marketing: mode === 'all' ? true : mode === 'reject' ? false : marketing,
      timestamp: new Date().toISOString(),
    };
    onAccept(consent);
  };

  return (
    <div
      style={{
        position: 'fixed' as const,
        inset: 0,
        background: 'rgba(0,0,0,.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
      }}>
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border2)',
          borderRadius: 20,
          padding: '28px 24px',
          maxWidth: 520,
          width: '100%',
          boxShadow: '0 -8px 40px rgba(0,0,0,.2)',
          maxHeight: '90vh',
          overflowY: 'auto' as const,
        }}>
        {showDsgvo
          ? <>
          <div style={{ ...(S.rowBetween as CSSProperties), marginBottom: 14 }}>
            <div
              style={{
                ...(S.row as CSSProperties),
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 16,
                fontWeight: 800,
                gap: 6,
              }}>
              <Icon name="shield" size={16} />
              {t('datenschutzTitle')}
            </div>
            <button
              onClick={() => setShowDsgvo(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 20,
                cursor: 'pointer',
                color: 'var(--muted)',
                lineHeight: 1,
              }}>
              ×
            </button>
          </div>
          <div
            style={{
              ...(S.caption as CSSProperties),
              maxHeight: '55vh',
              overflowY: 'auto' as const,
              lineHeight: 1.7,
              padding: '0 4px',
            }}>
            {getLegalSections('dsgvo').map((s, i) =>
              <div key={i} className="mb-16">
                <div
                  style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: 'var(--accent)' }}>
                  {s.t}
                </div>
                <div style={{ whiteSpace: 'pre-line' as const }}>
                  {s.p}
                </div>
              </div>,
            )}
          </div>
          <button
            onClick={() => setShowDsgvo(false)}
            style={{
              ...(S.btnP as CSSProperties),
              width: '100%',
              justifyContent: 'center',
              marginTop: 14,
              fontSize: 13,
            }}>
            {'\u2190 '}
            {t('zurueck')}
          </button>
        </>
          : <>
          <div
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 18,
              fontWeight: 800,
              marginBottom: 10,
              color: 'var(--text)',
            }}>
            {t('cookieTitle')}
          </div>
          <div
            style={{ ...(S.caption as CSSProperties), lineHeight: 1.7, marginBottom: 20 }}>
            {t('cookieText')}
          </div>
          <div style={{ ...(S.col as CSSProperties), gap: 12, marginBottom: 20 }}>
            <div
              style={{
                ...(S.rowBetween as CSSProperties),
                padding: '10px 14px',
                background: 'var(--surface)',
                borderRadius: 10,
                border: '1px solid var(--border2)',
              }}>
              <div>
                <div style={{ ...(S.labelLg as CSSProperties), color: 'var(--text)' }}>
                  <Icon name="lock" size={13} />
                  {' '}
                  {t('cookieRequired')}
                </div>
                <div className="t-muted-sub">
                  {t('cookieRequiredDesc')}
                </div>
              </div>
              <Toggle checked={true} disabled={true} />
            </div>
            <div
              style={{
                ...(S.rowBetween as CSSProperties),
                padding: '10px 14px',
                background: 'var(--surface)',
                borderRadius: 10,
                border: '1px solid var(--border2)',
              }}>
              <div>
                <div style={{ ...(S.labelLg as CSSProperties), color: 'var(--text)' }}>
                  <Icon name="chart" size={13} />
                  {' '}
                  {t('cookieAnalytics')}
                </div>
                <div className="t-muted-sub">
                  {t('cookieAnalyticsDesc')}
                </div>
              </div>
              <Toggle checked={analytics} onChange={setAnalytics} />
            </div>
            <div
              style={{
                ...(S.rowBetween as CSSProperties),
                padding: '10px 14px',
                background: 'var(--surface)',
                borderRadius: 10,
                border: '1px solid var(--border2)',
              }}>
              <div>
                <div style={{ ...(S.labelLg as CSSProperties), color: 'var(--text)' }}>
                  <Icon name="target" size={13} />
                  {' '}
                  {t('cookieMarketing')}
                </div>
                <div className="t-muted-sub">
                  {t('cookieMarketingDesc')}
                </div>
              </div>
              <Toggle checked={marketing} onChange={setMarketing} />
            </div>
          </div>
          <div style={{ ...(S.row as CSSProperties), gap: 8, flexWrap: 'wrap' as const }}>
            <button
              onClick={() => accept('reject')}
              style={{
                ...(S.btnG as CSSProperties),
                flex: 1,
                justifyContent: 'center',
                fontSize: 12,
                padding: '10px 14px',
                minWidth: 120,
              }}>
              {t('cookieRejectAll')}
            </button>
            <button
              onClick={() => accept('selected')}
              style={{
                ...(S.btnG as CSSProperties),
                flex: 1,
                justifyContent: 'center',
                fontSize: 12,
                padding: '10px 14px',
                minWidth: 120,
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
              }}>
              {t('cookieAcceptSelected')}
            </button>
            <button
              onClick={() => accept('all')}
              style={{
                ...(S.btnP as CSSProperties),
                flex: 1,
                justifyContent: 'center',
                fontSize: 12,
                padding: '10px 14px',
                minWidth: 120,
              }}>
              {t('cookieAcceptAll')}
            </button>
          </div>
          <div style={{ textAlign: 'center' as const, marginTop: 12 }}>
            <button
              onClick={() => setShowDsgvo(true)}
              style={{
                fontSize: 11,
                color: 'var(--accent)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 600,
                padding: 4,
              }}>
              <Icon name="shield" size={11} />
              {' '}
              {t('cookieDatenschutz')}
            </button>
          </div>
        </>}
      </div>
    </div>
  );
}
